import { FC } from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import Icon from 'src/components/Icon';
import * as S from './styles';

const msgs = defineMessages({
  dueForReview: { id: 'learnable_next_review_due', defaultMessage: 'Due for review' },
  dueInMinutes: {
    id: 'learnable_next_review_in_minutes',
    defaultMessage: 'Due in {count, plural, one {# minute} other {# minutes}}',
  },
  dueInHours: {
    id: 'learnable_next_review_in_hours',
    defaultMessage: 'Due in {count, plural, one {# hour} other {# hours}}',
  },
  dueInDays: {
    id: 'learnable_next_review_in_days',
    defaultMessage: 'Due in {count, plural, one {# day} other {# days}}',
  },
  dueInYears: {
    id: 'learnable_next_review_in_years',
    defaultMessage: 'Due in {count, plural, one {# year} other {# years}}',
  },
});

const MINUTE_MS = 60 * 1000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_YEAR = 365;

type NextReviewStatus =
  { isDue: true } | { isDue: false; message: MessageDescriptor; count: number };

const getNextReviewStatus = (nextReview: Date, now: Date): NextReviewStatus => {
  const diffMs = nextReview.getTime() - now.getTime();
  if (diffMs <= 0) return { isDue: true };

  const minutes = Math.ceil(diffMs / MINUTE_MS);
  if (minutes < MINUTES_PER_HOUR)
    return { isDue: false, message: msgs.dueInMinutes, count: minutes };

  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  if (hours < HOURS_PER_DAY) return { isDue: false, message: msgs.dueInHours, count: hours };

  const days = Math.floor(hours / HOURS_PER_DAY);
  if (days < DAYS_PER_YEAR) return { isDue: false, message: msgs.dueInDays, count: days };

  return { isDue: false, message: msgs.dueInYears, count: Math.floor(days / DAYS_PER_YEAR) };
};

interface Props {
  nextReview: string | Date;
  variant?: 'default' | 'blob';
}

export const NextReviewLabel: FC<Props> = ({ nextReview, variant = 'default' }) => {
  const intl = useIntl();
  const nextReviewDate = new Date(nextReview);

  if (Number.isNaN(nextReviewDate.getTime())) return null;

  const status = getNextReviewStatus(nextReviewDate, new Date());

  return (
    <S.Container $isDue={status.isDue} $isBlob={variant === 'blob'} data-testid="next-review-label">
      <Icon name="timer" />
      {status.isDue
        ? intl.formatMessage(msgs.dueForReview)
        : intl.formatMessage(status.message, { count: status.count })}
    </S.Container>
  );
};
